import { calculateGrossTotal, calculateNet } from '../utils'
import {
  calculateSelfServiceDelivery,
  createCheckout,
  createOrder,
  getDiscountCode,
  updateTransaction,
} from '../api'
import { useCallback, useContext, useEffect, useState } from 'react'
import { ClientContext } from '../contexts/client'
import { validateOrderData } from '../utils/validate-order-data'

const initialState = {
  contact: undefined,
  delivery: {
    type: 'pick-up',
  },
  discount: {
    code: null,
    discount: null, // type, value,
    error: null,
  },
  items: [],
}

export const CHECKOUT_STATUS = {
  available: 1,
  calculating: 2,
}

async function placeOrder({ data, shop, onComplete, onCancel }) {
  const { gateway, order, payment, transaction } = await createOrder(
    { ...data, __v: undefined, _id: undefined },
    {
      email: data.email,
      shop: shop._id,
    }
  )

  if (gateway === 'anm') {
    window.location.href = payment.redirect_url
    return
  }

  // eslint-disable-next-line no-undef
  const handler = PaystackPop.setup({
    ...payment,
    callback: async () => {
      await updateTransaction(transaction, {})

      await onComplete(order)
    },
    onClose: onCancel,
  })

  handler.openIframe()
}

async function submitCheckout({ data, shop, onComplete }) {
  const checkout = await createCheckout(data, {
    email: data.email,
    shop: shop._id,
  })

  onComplete(checkout)
}

export default function useCheckout(checkoutTemplate) {
  const [status, setStatus] = useState()
  const [detail, setDetail] = useState(initialState)
  const [error, setError] = useState(null)

  const { shop } = useContext(ClientContext)

  const calculateDelivery = useCallback(async () => {
    if (detail.delivery.address) {
      setStatus(CHECKOUT_STATUS.calculating)

      try {
        const rate = await calculateSelfServiceDelivery({
          client: shop._id,
          destination: detail.delivery.address.gps.coordinates,
        })

        setDetail((detail) => ({
          ...detail,
          delivery: {
            ...detail.delivery,
            rate: rate,
          },
        }))
      } catch (err) {
        setDetail((detail) => ({
          ...detail,
          delivery: {
            ...detail.delivery,
            rate: null,
          },
        }))

        setError({
          detail:
            'Failed to calculate delivery. Please try again or check About page on how to contact us.',
          type: 'delivery',
        })
      }

      setStatus(CHECKOUT_STATUS.available)
    }
  }, [detail.delivery.address, shop._id])

  const validateDiscountCode = useCallback(async () => {
    if (detail.discount.code) {
      setStatus(CHECKOUT_STATUS.calculating)

      try {
        const discountCode = await getDiscountCode(detail.discount.code, shop._id)

        if (!discountCode || discountCode.used === discountCode.limit || !discountCode.enabled) {
          setDetail((detail) => ({
            ...detail,
            discount: {
              ...detail.discount,
              error: 'Invalid discount code',
            },
          }))
        } else {
          setDetail((detail) => ({
            ...detail,
            discount: {
              ...discountCode,
            },
          }))
        }
      } catch (err) {
        setDetail((detail) => ({
          ...detail,
          discount: {
            ...detail.discount,
            error: 'Invalid discount code',
          },
        }))
      } finally {
        setStatus(CHECKOUT_STATUS.available)
      }
    } else {
      setDetail((detail) => ({
        ...detail,
        discount: {
          code: null,
          discount: null,
          error: null,
        },
      }))
    }
  }, [detail.discount.code, shop._id])

  const setItems = useCallback((items) => {
    setDetail((detail) => ({ ...detail, items }))
  }, [])

  const getGrossTotal = useCallback(() => {
    return calculateGrossTotal(detail)
  }, [detail])

  const getNetTotal = useCallback(
    (considerDeliveryPaymentOption = false) => {
      return calculateNet(detail, considerDeliveryPaymentOption)
    },
    [detail]
  )

  const setContact = useCallback((contact) => {
    setDetail((detail) => ({ ...detail, contact }))
  }, [])

  const setDeliveryAddress = useCallback((address) => {
    setError((error) => {
      if (error?.type === 'delivery') {
        return null
      }

      return error
    })

    setDetail((detail) => ({
      ...detail,
      delivery: { ...detail.delivery, address },
    }))
  }, [])

  const setDeliveryType = useCallback((type) => {
    setDetail((detail) => ({
      ...detail,
      delivery: {
        ...detail.delivery,
        type,
      },
    }))
  }, [])

  const setDiscountCode = useCallback((code) => {
    setError((error) => {
      if (error?.type === 'discount') {
        return null
      }

      return error
    })

    setDetail((detail) => ({ ...detail, discount: { error: null, ...detail.discount, code } }))
  }, [])

  const removeDiscountCode = useCallback(() => {
    setDiscountCode(null)
  }, [setDiscountCode])

  const reset = useCallback(() => {
    setDetail(initialState)
  }, [])

  const processCheckout = useCallback(
    async (extras, type = 'order', onComplete) => {
      const data = { ...detail, ...extras }

      setStatus(CHECKOUT_STATUS.calculating)

      const error = validateOrderData(data, type)
      setError(error)

      if (error) {
        setStatus(CHECKOUT_STATUS.available)
        return
      }

      try {
        const args = {
          data: { ...data, shop: shop._id },
          onCancel: () => {
            setStatus(CHECKOUT_STATUS.available)
          },
          onComplete: async (detail) => {
            await onComplete(detail)
            setStatus(CHECKOUT_STATUS.available)
          },
          shop,
        }

        if (type === 'order') {
          await placeOrder(args)
        } else {
          await submitCheckout(args)
        }
      } catch (err) {
        const message = err.response?.data?.message

        if (message?.startsWith('[ACC_EXISTS]')) {
          setError({
            detail: 'An account with this email exists. Login instead',
            type: 'authentication',
          })
        } else {
          setError({
            detail:
              message ||
              (type === 'checkout'
                ? 'Failed to create checkout. Please try again'
                : 'Failed to place order. Please try again!'),
            type: 'general',
          })
        }

        setStatus(CHECKOUT_STATUS.available)
      }
    },
    [detail, shop]
  )

  useEffect(() => {
    calculateDelivery()
  }, [calculateDelivery])

  useEffect(() => {
    validateDiscountCode()
  }, [validateDiscountCode])

  useEffect(
    function setUp() {
      setDetail((detail) => ({
        ...detail,
        ...checkoutTemplate,
      }))
    },
    [checkoutTemplate]
  )

  return {
    detail,
    error,
    getGrossTotal,
    getNetTotal,
    processCheckout,
    removeDiscountCode,
    reset,
    setContact,
    setDeliveryAddress,
    setDeliveryType,
    setDiscountCode,
    setItems,
    status,
  }
}
